import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Users,
  ShieldCheck,
  ShieldAlert,
  Edit2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Lock,
  Unlock,
  Key,
  UserCheck,
  AlertCircle,
  RotateCcw,
  SlidersHorizontal,
  UserX,
  Search,
  UserPlus,
  Send,
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { authService } from '@/services/authService';
import { PageHeader } from '@/components/common/PageHeader';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import { KpiCardGroup } from '@/components/common/KpiCardGroup';
import { KpiCard } from '@/components/common/KpiCard';
import { FilterBar } from '@/components/common/FilterBar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingState } from '@/components/common/LoadingState';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { useAuth } from '@/context/AuthContext';
import { formatDate } from '@/lib/utils';
import {
  UserProfile,
  UserRole,
  UserPermissions,
  ADMIN_FULL_PERMISSIONS,
  OPERATIONS_PERMISSIONS,
  VIEWER_PERMISSIONS,
  DEFAULT_DENY_PERMISSIONS,
} from '@/types/auth';

interface ModulePermissionDef {
  key: keyof UserPermissions;
  name: string;
  actions: Array<{ key: string; label: string }>;
}

const MODULE_DEFINITIONS: ModulePermissionDef[] = [
  {
    key: 'production_cycles',
    name: 'Production Cycles & Herd',
    actions: [
      { key: 'view', label: 'View Cycles' },
      { key: 'edit', label: 'Create / Edit Cycles' },
    ],
  },
  {
    key: 'feed',
    name: 'Feed Management',
    actions: [
      { key: 'view', label: 'View Deliveries' },
      { key: 'enter', label: 'Receive Invoices' },
      { key: 'transfer', label: 'Cycle Transfers' },
    ],
  },
  {
    key: 'sales',
    name: 'Pig Sales',
    actions: [
      { key: 'view', label: 'View Sales' },
      { key: 'enter', label: 'Record Weighments & Sales' },
    ],
  },
  {
    key: 'settlements',
    name: 'Grower Settlements',
    actions: [
      { key: 'view', label: 'View Settlements' },
      { key: 'create', label: 'Draft Calculations' },
      { key: 'finalize', label: 'Freeze / Finalize' },
    ],
  },
  {
    key: 'ledger',
    name: 'General Ledger',
    actions: [
      { key: 'view', label: 'View Book of Accounts' },
      { key: 'export', label: 'Export Ledger (CSV/Excel)' },
    ],
  },
  {
    key: 'review_resolve',
    name: 'Review & Resolve',
    actions: [
      { key: 'view', label: 'View Exceptions' },
      { key: 'resolve', label: 'Resolve Issues' },
    ],
  },
  {
    key: 'reports',
    name: 'Reports & Financials',
    actions: [{ key: 'view', label: 'View Reports & Analytics' }],
  },
  {
    key: 'master_tables',
    name: 'Master Data',
    actions: [
      { key: 'view', label: 'View Master Lists' },
      { key: 'edit', label: 'Create / Edit Master Data' },
    ],
  },
  {
    key: 'users',
    name: 'Users & Permissions',
    actions: [{ key: 'admin', label: 'User Administration' }],
  },
];

export const UsersPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canAdminUsers = hasPermission('users', 'admin');

  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Edit Modal State
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [formRole, setFormRole] = useState<UserRole>('operations');
  const [formActive, setFormActive] = useState<boolean>(true);
  const [formPermissions, setFormPermissions] = useState<UserPermissions>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Add User (Invitation) Modal State
  const [isAddOpen, setIsAddOpen] = useState<boolean>(false);
  const [addFullName, setAddFullName] = useState<string>('');
  const [addEmail, setAddEmail] = useState<string>('');
  const [addRole, setAddRole] = useState<UserRole>('operations');
  const [inviting, setInviting] = useState<boolean>(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState<string | null>(null);

  const handleOpenAddUser = () => {
    setAddFullName('');
    setAddEmail('');
    setAddRole('operations');
    setAddError(null);
    setAddSuccess(null);
    setIsAddOpen(true);
  };

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanName = addFullName.trim();
    const cleanEmail = addEmail.trim();

    if (!cleanName || !cleanEmail) {
      setAddError('Full name and email address are required.');
      return;
    }

    setInviting(true);
    setAddError(null);
    setAddSuccess(null);

    try {
      await authService.inviteUser({
        fullName: cleanName,
        email: cleanEmail,
        role: addRole,
      });

      setAddSuccess(`Invitation sent successfully to ${cleanEmail}.`);
      await loadUsers();
      setTimeout(() => {
        setIsAddOpen(false);
      }, 1200);
    } catch (err: any) {
      setAddError(err.message || 'Failed to send user invitation.');
    } finally {
      setInviting(false);
    }
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('role', { ascending: true });

      if (!error && data) {
        setProfiles(data as UserProfile[]);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // KPI Calculations
  const kpis = useMemo(() => {
    const total = profiles.length;
    const active = profiles.filter((p) => p.is_active !== false).length;
    const admins = profiles.filter((p) => p.role === 'admin').length;
    const ops = profiles.filter((p) => p.role === 'operations').length;

    return {
      total,
      active,
      admins,
      ops,
    };
  }, [profiles]);

  // Filtered Users
  const filteredProfiles = useMemo(() => {
    return profiles.filter((p) => {
      if (roleFilter !== 'all' && p.role !== roleFilter) return false;
      if (statusFilter === 'active' && p.is_active === false) return false;
      if (statusFilter === 'inactive' && p.is_active !== false) return false;

      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase().trim();
        const name = (p.full_name || '').toLowerCase();
        const email = (p.email || '').toLowerCase();
        const role = (p.role || '').toLowerCase();
        return name.includes(q) || email.includes(q) || role.includes(q);
      }

      return true;
    });
  }, [profiles, searchTerm, roleFilter, statusFilter]);

  const handleResetFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
  };

  const isFiltered = searchTerm !== '' || roleFilter !== 'all' || statusFilter !== 'all';

  const openEditUser = (profile: UserProfile) => {
    setSelectedUser(profile);
    setFormRole(profile.role);
    setFormActive(profile.is_active !== false);

    // Initialize permissions
    const existingPerms = profile.permissions || {};
    setFormPermissions(JSON.parse(JSON.stringify(existingPerms)));
    setErrorMsg(null);
    setIsEditOpen(true);
  };

  const handlePermissionToggle = (moduleKey: keyof UserPermissions, actionKey: string) => {
    setFormPermissions((prev) => {
      const copy = { ...prev };
      const currentModule = (copy[moduleKey] as any) || {};
      const nextVal = !currentModule[actionKey];

      copy[moduleKey] = {
        ...currentModule,
        [actionKey]: nextVal,
      };

      // If action is enabled, ensure 'view' is also enabled
      if (nextVal && actionKey !== 'view') {
        copy[moduleKey] = {
          ...copy[moduleKey],
          view: true,
        };
      }

      return copy;
    });
  };

  const applyPreset = (preset: 'admin' | 'operations' | 'viewer') => {
    if (preset === 'admin') {
      setFormRole('admin');
      setFormPermissions(JSON.parse(JSON.stringify(ADMIN_FULL_PERMISSIONS)));
    } else if (preset === 'operations') {
      setFormRole('operations');
      setFormPermissions(JSON.parse(JSON.stringify(OPERATIONS_PERMISSIONS)));
    } else if (preset === 'viewer') {
      setFormRole('viewer');
      setFormPermissions(JSON.parse(JSON.stringify(VIEWER_PERMISSIONS)));
    }
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    setSaving(true);
    setErrorMsg(null);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          role: formRole,
          is_active: formActive,
          permissions: formPermissions,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedUser.id);

      if (error) throw error;

      setIsEditOpen(false);
      setSelectedUser(null);
      await loadUsers();
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to update user profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* 1. Standard PageHeader */}
      <PageHeader
        title="Users & Modular Permissions"
        subtitle="Manage system profiles, role assignments, and fine-grained module permission checkboxes."
        icon={ShieldCheck}
        helpScreenKey="users"
        actions={
          <div className="flex items-center gap-2">
            <ActionTooltip content="Reload registered user profiles, status, and role assignments.">
              <Button
                variant="outline"
                size="sm"
                onClick={loadUsers}
                disabled={loading}
                className="text-xs h-9 min-h-[44px] gap-1.5 border-slate-300 hover:bg-slate-50"
              >
                <RotateCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </Button>
            </ActionTooltip>

            {canAdminUsers && (
              <ActionTooltip content="Invite a new user to VSB Farm via email.">
                <Button
                  size="sm"
                  onClick={handleOpenAddUser}
                  className="text-xs h-9 min-h-[44px] gap-1.5 bg-emerald-800 hover:bg-emerald-900 text-white font-semibold shadow-xs"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>+ Add User</span>
                </Button>
              </ActionTooltip>
            )}
          </div>
        }
      />

      {/* 2. Standard 4-Card KpiCardGroup */}
      <KpiCardGroup columns={4}>
        <KpiCard
          title="Total Registered Users"
          value={kpis.total.toString()}
          subtitle="System user accounts"
          icon={Users}
          variant="default"
        />
        <KpiCard
          title="Active Accounts"
          value={kpis.active.toString()}
          subtitle="Authorized for system login"
          icon={CheckCircle2}
          variant="emerald"
        />
        <KpiCard
          title="Administrators"
          value={kpis.admins.toString()}
          subtitle="Unrestricted system access"
          icon={Key}
          variant="indigo"
        />
        <KpiCard
          title="Operations & Staff"
          value={kpis.ops.toString()}
          subtitle="Field staff & operators"
          icon={UserCheck}
          variant="amber"
        />
      </KpiCardGroup>

      {/* Admin Notice Banner */}
      <div className="p-3.5 bg-slate-900 text-white rounded-xl flex items-start gap-3 shadow-xs border border-slate-800">
        <Key className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs space-y-0.5">
          <span className="font-bold text-white block">Unrestricted Administrator Access</span>
          <p className="text-slate-300 leading-relaxed">
            Users assigned the <span className="font-mono font-bold text-emerald-400">admin</span> role (including Cez / Senior Admins) receive unrestricted access across all operational modules, workflows, and database actions regardless of checkbox configuration.
          </p>
        </div>
      </div>

      {/* 3. FilterBar with Search and Role/Status Filter */}
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search users by name, email address, role..."
        onReset={handleResetFilters}
        showReset={isFiltered}
      >
        <div className="w-full sm:w-44">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="w-full h-10 min-h-[44px] rounded-md border border-slate-300 bg-white px-3 text-xs font-medium text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
          >
            <option value="all">All Roles</option>
            <option value="admin">Administrator</option>
            <option value="operations">Operations</option>
            <option value="viewer">Viewer</option>
          </select>
        </div>

        <div className="w-full sm:w-40">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full h-10 min-h-[44px] rounded-md border border-slate-300 bg-white px-3 text-xs font-medium text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
      </FilterBar>

      {/* 4. Desktop Table & Mobile Cards */}
      {loading ? (
        <div className="py-12 bg-white rounded-xl border border-slate-200 shadow-sm">
          <LoadingState message="Loading user profiles..." />
        </div>
      ) : filteredProfiles.length === 0 ? (
        <div className="py-12 bg-white rounded-xl border border-slate-200 shadow-sm">
          <EmptyState
            icon={Users}
            title="No User Profiles Found"
            description="Try adjusting your search query or role/status filters."
          />
        </div>
      ) : (
        <div>
          {/* Desktop Table (md:table) */}
          <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-slate-100/70 text-slate-700 font-semibold border-b border-slate-200 select-none">
                  <tr>
                    <th className="py-3 px-3.5 whitespace-nowrap">User Profile</th>
                    <th className="py-3 px-3.5 whitespace-nowrap">Email Address</th>
                    <th className="py-3 px-3.5 text-center whitespace-nowrap">Role</th>
                    <th className="py-3 px-3.5 text-center whitespace-nowrap">Status</th>
                    <th className="py-3 px-3.5 whitespace-nowrap">Registered</th>
                    {canAdminUsers && <th className="py-3 px-3.5 text-center whitespace-nowrap w-28">Permissions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white font-sans text-xs">
                  {filteredProfiles.map((profile) => {
                    const isActive = profile.is_active !== false;
                    return (
                      <tr key={profile.id} className="hover:bg-slate-50 transition-colors">
                        <td className="py-3 px-3.5 font-bold text-slate-900 whitespace-nowrap">
                          {profile.full_name || 'Unnamed User'}
                        </td>
                        <td className="py-3 px-3.5 font-mono text-slate-600 whitespace-nowrap">
                          {profile.email}
                        </td>
                        <td className="py-3 px-3.5 text-center whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                              profile.role === 'admin'
                                ? 'bg-slate-900 text-white'
                                : profile.role === 'operations'
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                : 'bg-sky-100 text-sky-800 border border-sky-300'
                            }`}
                          >
                            <ShieldCheck className="w-3 h-3" />
                            {profile.role}
                          </span>
                        </td>
                        <td className="py-3 px-3.5 text-center whitespace-nowrap">
                          {isActive ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" /> Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> Inactive
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-3.5 text-slate-500 font-mono text-[11px] whitespace-nowrap">
                          {formatDate(profile.created_at)}
                        </td>
                        {canAdminUsers && (
                          <td className="py-3 px-3.5 text-center whitespace-nowrap">
                            <ActionTooltip content="Edit user profile, role security, and modular permission checkboxes.">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditUser(profile)}
                                className="h-8 text-xs font-semibold gap-1 border-slate-300 hover:bg-slate-100 text-slate-700"
                              >
                                <Edit2 className="w-3.5 h-3.5 text-slate-600" />
                                <span>Configure</span>
                              </Button>
                            </ActionTooltip>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards (md:hidden) */}
          <div className="md:hidden space-y-3">
            {filteredProfiles.map((profile) => {
              const isActive = profile.is_active !== false;
              return (
                <div
                  key={profile.id}
                  className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-bold text-sm text-slate-900">
                        {profile.full_name || 'Unnamed User'}
                      </div>
                      <div className="font-mono text-xs text-slate-500 mt-0.5">
                        {profile.email}
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        profile.role === 'admin'
                          ? 'bg-slate-900 text-white'
                          : profile.role === 'operations'
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                          : 'bg-sky-100 text-sky-800 border border-sky-300'
                      }`}
                    >
                      <ShieldCheck className="w-3 h-3" />
                      {profile.role}
                    </span>
                  </div>

                  <div className="space-y-1 bg-slate-50 p-3 rounded-lg border border-slate-100 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Status:</span>
                      {isActive ? (
                        <span className="font-semibold text-emerald-700 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" /> Active
                        </span>
                      ) : (
                        <span className="font-semibold text-slate-400 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Registered:</span>
                      <span className="font-mono text-slate-700">{formatDate(profile.created_at)}</span>
                    </div>
                  </div>

                  {canAdminUsers && (
                    <div className="pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditUser(profile)}
                        className="w-full h-11 min-h-[44px] text-xs font-semibold text-slate-700 border-slate-300 justify-center gap-1.5"
                      >
                        <Edit2 className="w-4 h-4 text-slate-600" />
                        <span>Configure Permissions</span>
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Permissions Configuration Modal */}
      {isEditOpen && selectedUser && (
        <Dialog open={true} onOpenChange={() => setIsEditOpen(false)}>
          <DialogContent className="max-w-2xl p-6 bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader className="border-b border-slate-200 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-emerald-700" />
                    <span>Configure Permissions — {selectedUser.full_name}</span>
                  </DialogTitle>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">{selectedUser.email}</p>
                </div>
              </div>
            </DialogHeader>

            <form onSubmit={handleSaveUser} className="space-y-5 pt-3 text-xs">
              {errorMsg && (
                <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-md">
                  {errorMsg}
                </div>
              )}

              {/* Role & Status Controls */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3.5 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">System Role Assignment</label>
                  <select
                    value={formRole}
                    onChange={(e) => setFormRole(e.target.value as UserRole)}
                    className="w-full h-10 min-h-[44px] rounded-md border border-slate-300 bg-white px-3 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  >
                    <option value="admin">Administrator (Full Access)</option>
                    <option value="operations">Operations (Field Staff)</option>
                    <option value="viewer">Viewer (Read Only)</option>
                  </select>
                </div>

                <div>
                  <label className="font-bold text-slate-700 block mb-1">Account Login Status</label>
                  <div className="flex items-center gap-4 mt-2">
                    <label className="flex items-center gap-1.5 cursor-pointer min-h-[36px]">
                      <input
                        type="radio"
                        name="account_status"
                        checked={formActive}
                        onChange={() => setFormActive(true)}
                        className="text-emerald-600 h-4 w-4"
                      />
                      <span className="font-semibold text-slate-800 text-xs">Active</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer min-h-[36px]">
                      <input
                        type="radio"
                        name="account_status"
                        checked={!formActive}
                        onChange={() => setFormActive(false)}
                        className="text-slate-600 h-4 w-4"
                      />
                      <span className="font-semibold text-slate-600 text-xs">Disabled / Inactive</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Quick Presets */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-1">
                <span className="font-bold text-slate-800 uppercase tracking-wider text-[11px]">
                  Module-Level Permission Matrix
                </span>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-slate-400 mr-1">Presets:</span>
                  <button
                    type="button"
                    onClick={() => applyPreset('admin')}
                    className="px-3 py-1 text-xs font-bold rounded bg-slate-900 text-white hover:bg-slate-800 min-h-[36px]"
                  >
                    Admin
                  </button>
                  <button
                    type="button"
                    onClick={() => applyPreset('operations')}
                    className="px-3 py-1 text-xs font-bold rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border border-emerald-300 min-h-[36px]"
                  >
                    Operations
                  </button>
                  <button
                    type="button"
                    onClick={() => applyPreset('viewer')}
                    className="px-3 py-1 text-xs font-bold rounded bg-sky-100 text-sky-800 hover:bg-sky-200 border border-sky-300 min-h-[36px]"
                  >
                    Viewer
                  </button>
                </div>
              </div>

              {/* Module Checkbox Grid */}
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-200 overflow-hidden">
                {MODULE_DEFINITIONS.map((mod) => {
                  const modPerms = (formPermissions[mod.key] as any) || {};

                  return (
                    <div key={mod.key} className="p-3 bg-white hover:bg-slate-50/70 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <span className="font-bold text-slate-900 text-xs w-48">{mod.name}</span>
                        <div className="flex flex-wrap items-center gap-4">
                          {mod.actions.map((act) => {
                            const isChecked = formRole === 'admin' || Boolean(modPerms[act.key]);

                            return (
                              <label
                                key={act.key}
                                className={`flex items-center gap-2 select-none min-h-[36px] ${
                                  formRole === 'admin' ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  disabled={formRole === 'admin'}
                                  onChange={() => handlePermissionToggle(mod.key, act.key)}
                                  className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                                />
                                <span className="text-slate-700 text-xs font-medium">{act.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <DialogFooter className="mt-4 pt-3 border-t border-slate-200 flex flex-col sm:flex-row justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditOpen(false)}
                  className="min-h-[44px] text-xs font-semibold text-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={saving}
                  className="min-h-[44px] bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-semibold"
                >
                  {saving ? 'Saving...' : 'Save Permissions'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Add User (Invitation) Modal */}
      {isAddOpen && (
        <Dialog open={true} onOpenChange={() => !inviting && setIsAddOpen(false)}>
          <DialogContent className="max-w-md p-6 bg-white">
            <DialogHeader className="border-b border-slate-200 pb-3">
              <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-emerald-700" />
                <span>Invite New User</span>
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Send an email invitation to provision a new user account.
              </p>
            </DialogHeader>

            <form onSubmit={handleSendInvitation} className="space-y-4 pt-3 text-xs">
              {addError && (
                <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-md flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{addError}</span>
                </div>
              )}

              {addSuccess && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{addSuccess}</span>
                </div>
              )}

              <div>
                <label className="font-bold text-slate-700 block mb-1">
                  Full Name <span className="text-rose-600">*</span>
                </label>
                <Input
                  type="text"
                  required
                  value={addFullName}
                  onChange={(e) => setAddFullName(e.target.value)}
                  placeholder="e.g. Cez VSB Admin"
                  disabled={inviting}
                  className="h-10 min-h-[44px] text-xs"
                />
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">
                  Email Address <span className="text-rose-600">*</span>
                </label>
                <Input
                  type="email"
                  required
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="e.g. user@vsbfarm.ph"
                  disabled={inviting}
                  className="h-10 min-h-[44px] text-xs font-mono"
                />
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">
                  System Role Assignment <span className="text-rose-600">*</span>
                </label>
                <select
                  value={addRole}
                  onChange={(e) => setAddRole(e.target.value as UserRole)}
                  disabled={inviting}
                  className="w-full h-10 min-h-[44px] rounded-md border border-slate-300 bg-white px-3 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                >
                  <option value="admin">Administrator (Full Access)</option>
                  <option value="operations">Operations (Field Staff)</option>
                  <option value="viewer">Viewer (Read Only)</option>
                </select>
              </div>

              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 text-[11px] leading-relaxed">
                <span className="font-semibold text-slate-800 block mb-0.5">Password & Activation Notice:</span>
                The invited user will receive a secure email link to set their own password and activate their account. Detailed module permissions can be customized after creation via the <span className="font-semibold text-slate-800">Configure</span> button.
              </div>

              <DialogFooter className="mt-4 pt-3 border-t border-slate-200 flex flex-col sm:flex-row justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddOpen(false)}
                  disabled={inviting}
                  className="min-h-[44px] text-xs font-semibold text-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={inviting}
                  className="min-h-[44px] bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-semibold gap-1.5"
                >
                  {inviting ? (
                    <>
                      <RotateCcw className="w-3.5 h-3.5 animate-spin" />
                      <span>Sending Invitation...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      <span>Send Invitation</span>
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
